/**
 * DataLayer Helper for Shopify
 * 
 * A lightweight library for tracking user interactions in Shopify stores.
 * Uses data attributes for declarative tracking and Shopify analytics for publishing events.
 * Should be added as higher after the opening <body> tag.
 */
class DataLayerHelper {
  constructor(options = {}) {
    this.options = {
      debug: options.debug || false,
      ...options
    };

    // Store for pre-prepared data
    this.dataStore = {};
    
    // Track elements that have been viewed
    this.viewedElements = new WeakSet();
    
    // Store original dataLayer push method
    this.originalDataLayerPush = null;
    
    // Initialize tracking
    this.init();
  }

  /**
   * Initialize all tracking functionality
   */
  init() {
    // Initialize click tracking
    document.body.addEventListener('click', this.handleClickEvent.bind(this));
    
    // Initialize custom click tracking
    this.initCustomClickTracking();
    
    // Initialize view tracking
    this.initViewTracking();
    
    // Initialize mutation observer for dynamic content
    this.initMutationObserver();
    
    // Initialize cart events
    this.initCartEvents();

    // Initialize global events
    this.initGlobalEvents();

    // Initialize blog specific events
    this.initBlogEvents();

    // Initialize account login specific events
    this.initAccountLoginEvents();

    // Initialize account register specific events
    this.initAccountRegisterEvents();

    // Initialize AI coach specific events
    this.initAICoachEvents();

    // Initialize PC specific events
    this.initPCEvents();

    // Initialize theme dataLayer tracking
    this.initThemeDataLayerTracking();

    this.log('DataLayerHelper initialized');
  }

  /**
   * Initialize theme dataLayer tracking by overriding the push method
   */
  initThemeDataLayerTracking() {
    let attempt = 1;
    const MAX_ATTEMPTS = 5;

    const interval = setInterval(() => {
      console.log('attemp', attempt);
      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        this.log('Initializing theme dataLayer tracking');

        // Process any existing items in the dataLayer
        if (window.dataLayer.length > 0) {
          this.log(`Processing ${window.dataLayer.length} existing dataLayer items`);
          window.dataLayer.forEach(item => this.processDataLayerPush(item));
        }

        // Store the original push method
        this.originalDataLayerPush = window.dataLayer.push;

        // Override the push method
        window.dataLayer.push = (...args) => {
          // Call the original push method to maintain normal functionality
          const result = this.originalDataLayerPush.apply(window.dataLayer, args);

          // Process the pushed data for our tracking
          this.processDataLayerPush(args[0]);

          // Return the original result
          return result;
        };

        this.log('Theme dataLayer tracking initialized');
        clearInterval(interval);
      } else if (attempt < MAX_ATTEMPTS) {
        attempt++;
      } else {
        this.log('Theme dataLayer tracking failed');
        clearInterval(interval);
      }
    }, 500);
  }

  /**
   * Process data pushed to the dataLayer
   * @param {Object} data - The data pushed to the dataLayer
   */
  processDataLayerPush(data) {
    if (!data) return;
    
    try {
      // Skip our own events to avoid infinite loops
      if (data.event_source === 'shopify_pixel') return;

      this.log('Processing dataLayer push:', data);

      // Only process events
      if (data.event) {
        // Skip GTM initialization events
        if (data.event === 'gtm.js' || data.event === 'gtm.dom' || data.event === 'gtm.load') {
          return;
        }
        
        // Track the event
        this.track(data.event, { ...data });
      }
    } catch (error) {
      this.log('Error processing dataLayer push:', error);
    }
  }

  /**
   * Initialize custom click tracking for storefront elements
   */
  initCustomClickTracking() {
    // Use a single event listener with debounce to improve performance
    let clickTimeout = null;
    const CLICK_DELAY = 300; // ms
    
    document.body.addEventListener('click', (event) => {
      // Clear any pending timeout
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
      
      // Set a new timeout to debounce rapid clicks
      clickTimeout = setTimeout(() => {
        // Skip tracking for elements that already have data-dl-onclick
        // to avoid duplicate tracking
        if (event.target.closest('[data-dl-onclick]')) {
          return;
        }
        
        // Get the most relevant element (the clicked element or its closest interactive parent)
        const relevantElement = this.getRelevantElement(event.target);
        
        // Skip if no relevant element found
        if (!relevantElement) {
          return;
        }
        
        // Check if the element is a link
        const isLink = relevantElement.tagName === 'A' || relevantElement.tagName === 'BUTTON' && relevantElement.getAttribute('href');
        
        if (isLink) {
          const clickData = this.extractClickData(relevantElement, true);
          this.track('custom_click_link_storefront', {
            data: clickData
          });
        } else {
          // Only track interactive elements like buttons, inputs, etc.
          const clickData = this.extractClickData(relevantElement, false);
          this.track('custom_click_storefront', {
            data: clickData
          });
        }
      }, CLICK_DELAY);
    });
  }

  /**
   * Get the most relevant element for click tracking
   * @param {HTMLElement} element - The clicked element
   * @returns {HTMLElement|null} - The most relevant element or null
   */
  getRelevantElement(element) {
    // List of interactive elements we want to track
    const interactiveElements = [
      'A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 
      'DETAILS', 'SUMMARY', 'VIDEO', 'AUDIO'
    ];
    
    // Check if the element itself is interactive
    if (interactiveElements.includes(element.tagName)) {
      return element;
    }
    
    // Check for elements with role attributes that make them interactive
    if (element.getAttribute('role') && 
        ['button', 'link', 'checkbox', 'radio', 'tab', 'menuitem'].includes(element.getAttribute('role'))) {
      return element;
    }
    
    // Check if the element has click-related attributes
    if (element.hasAttribute('onclick') || element.hasAttribute('data-action')) {
      return element;
    }
    
    // Look for closest interactive parent
    const closestInteractive = interactiveElements
      .map(tag => element.closest(tag))
      .filter(Boolean)[0];
    
    if (closestInteractive) {
      return closestInteractive;
    }
    
    // If we get here, check if the element has a class that suggests interactivity
    const interactiveClasses = ['btn', 'button', 'link', 'clickable', 'selectable'];
    const hasInteractiveClass = Array.from(element.classList)
      .some(cls => interactiveClasses.some(iCls => cls.includes(iCls)));
    
    if (hasInteractiveClass) {
      return element;
    }
    
    // No relevant element found
    return null;
  }

  /**
   * Extract click data from an element
   * @param {HTMLElement} element - The clicked element
   * @param {boolean} isLink - Whether the element is a link
   * @returns {Object} - Click data object
   */
  extractClickData(element, isLink) {
    // Generate DOM path
    const domPath = this.getDomPath(element);
    
    // Get element ID
    const clickId = element.id || '';
    
    // Get element classes
    const clickClasses = Array.from(element.classList).join(' ');
    
    // Get element text content - clean up whitespace and limit length
    let clickText = (element.textContent || '').replace(/\s+/g, ' ').trim();
    if (clickText.length > 100) {
      clickText = `${clickText.substring(0, 97)  }...`;
    }
    
    // Create data object
    const clickData = {
      click_element: `HTML: ${domPath}`,
      click_id: clickId,
      click_classes: clickClasses,
      click_text: clickText,
      click_target: '',
      click_url: ''
    };
    
    // Add link-specific data if applicable
    if (isLink) {
      clickData.click_target = element.getAttribute('target') || '';
      clickData.click_url = element.getAttribute('href') || '';
    }
    
    return clickData;
  }

  /**
   * Get DOM path for an element
   * @param {HTMLElement} element - The element to get path for
   * @returns {string} - DOM path string
   */
  getDomPath(element) {
    const path = [];
    let currentElement = element;
    
    while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
      let selector = currentElement.nodeName.toLowerCase();
      
      if (currentElement.id) {
        selector += `#${currentElement.id}`;
      } else if (currentElement.className && typeof currentElement.className === 'string') {
        selector += `.${currentElement.className.split(/\s+/).join('.')}`;
      }
      
      path.unshift(selector);
      currentElement = currentElement.parentNode;
    }
    
    return path.join(' > ');
  }

  /**
   * Initialize intersection observer for view tracking
   */
  initViewTracking() {
    if (!('IntersectionObserver' in window)) {
      this.log('IntersectionObserver not supported in this browser');
      return;
    }
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          
          // Only track view once per element
          if (!this.viewedElements.has(element)) {
            const { dlOnview: eventName, dlId: sectionId } = element.dataset;
            
            if (eventName && sectionId) {
              const eventData = this.dataStore[sectionId] || {};
              this.track(eventName, eventData);
              this.viewedElements.add(element);
              this.intersectionObserver.unobserve(element);
            }
          }
        }
      });
    });
    
    // Observe all initial elements with data-dl-onview
    document.querySelectorAll('[data-dl-onview]').forEach(el => 
      this.intersectionObserver.observe(el)
    );
  }

  /**
   * Initialize mutation observer for dynamic content
   */
  initMutationObserver() {
    if (!('MutationObserver' in window)) {
      this.log('MutationObserver not supported in this browser');
      return;
    }
    
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            this.processNewNode(node);
          });
        }
      });
    });
    
    // Start observing the document
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize cart events
   */
  initCartEvents() {
    // Listen for cart drawer opening
    document.addEventListener('on:cart-drawer:after-open', () => {
      // run script to get cart data
      const script = document.querySelector('[data-dl-cart-data-script]');
      if (script) {
        eval(script.textContent);
      }

      const cartData = this.dataStore['cart-data'];
      if (cartData) {
        this.track('view_cart', cartData);
      }
    });
  }

  /**
   * Initialize page global events
   */
  /**
   * Initialize blog-specific event tracking
   */
  initBlogEvents() {
    document.body.addEventListener('click', (event) => {
      const filterElement = event.target.closest('[data-blog-filter-option]');
      if (filterElement) {
        const blogCategory = filterElement.dataset.dlBlogCategory;
        const blogFilter = filterElement.dataset.dlBlogFilter;

        if (blogCategory && blogFilter) {
          this.track('blog_filter_added', {
            blog_category: blogCategory,
            blog_filter: blogFilter
          });
        } else {
          this.log('Missing data-dl-blog-category or data-dl-blog-filter for blog_filter_added event', filterElement);
        }
      }
    });
  }

  /**
   * Initialize page global events
   */
  initGlobalEvents() {
    this.track('page_view', {
      page_category : window?.ShopifyAnalytics?.meta?.page?.pageType || "", //String, required
      site_language : window?.Shopify?.locale || "", //String, required
    });
  }

  /**
   * Initialize account-login event tracking
   */
  initAccountLoginEvents() {
    const cookie = document.cookie.split(';').find(row => row.trim().startsWith('login_page_visited='));
    if (cookie) {
      document.cookie = 'login_page_visited=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      if (window.location.pathname === '/account') {
        this.track('login_success', {});
      }
    }

    if (window.location.pathname === '/account/login') {
      document.cookie = `login_page_visited=true; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/;`;
    }
  }

  /**
   * Initialize account-register event tracking
   */
  initAccountRegisterEvents() {
    const cookie = document.cookie.split(';').find(row => row.trim().startsWith('register_page_visited='));
    if (cookie) {
      document.cookie = 'register_page_visited=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      if (window.customerId) {
        this.track('create_account_success', {
          newsletter_opt_in: 'False' // we don't have checkbox
        });
      }
    }

    if (window.location.pathname === '/account/register') {
      document.cookie = `register_page_visited=true; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/;`;
    }
  }

  /**
   * Initialize aicoach-specific event tracking
   */
  initAICoachEvents() {
    const chat = window.fcWidget;
    if (!chat) return;

    chat.on("widget:opened", () => {
      this.track('aicoach_interaction', {
        chatbot_action: 'opened',
        timestamp: Date.now()
      });
    });

    chat.on("widget:closed", () => {
      this.track('aicoach_interaction', {
        chatbot_action: 'closed',
        timestamp: Date.now()
      });
    });
  }

  /**
   * Initialize PC events
   */
  initPCEvents() {
    const handlePCLinks = (container = document.body) => {
      if (!container) return;
      container.querySelectorAll('a[href*="/pages/protein-calculator"]').forEach(link => {
        let clickType = link.dataset.dlClickType ?? "Navigation menu"; // FloatingIcon for icon

        if (link.closest('predictive-search')) {
          clickType = "Predictive search";
        }

        link.addEventListener('click', () => {
          this.track('P_C_Click', {
            pageType: window?.ShopifyAnalytics?.meta?.page?.pageType || "",
            clickType
          });
        });
      });
    };

    document.addEventListener('DOMContentLoaded', () => {
      handlePCLinks();

      const predictiveSearch = document.querySelector('predictive-search');

      if (predictiveSearch) {
        predictiveSearch.addEventListener('psearch:done', () => {
          handlePCLinks(predictiveSearch);
        });
      }
    });
  }

  /**
   * Process a newly added node for tracking elements
   * @param {Node} node - The node to process
   */
  processNewNode(node) {
    // Only process element nodes
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    
    // Check if the node itself has tracking attributes
    if (node.hasAttribute?.('data-dl-onview')) {
      this.intersectionObserver.observe(node);
    }
    
    // Check children of the node
    node.querySelectorAll?.('[data-dl-onview]').forEach(element => {
      this.intersectionObserver.observe(element);
    });
  }

  /**
   * Handle click events for tracking
   * @param {MouseEvent} event - The click event
   */
  handleClickEvent(event) {
    try {
      const target = event.target.closest('[data-dl-onclick]');
      if (!target) return;

      const { dlOnclick: eventName, dlId: sectionId, index = '0', dlData = '{}' } = target.dataset;

      if (!eventName) return;

      let eventDataForTracking;

      if (eventName === 'blog_share') {
        // Construct payload specifically for blog_share as per user request
        eventDataForTracking = {
          blog_category: target.dataset.dlBlogCategory,
          blog_name: target.dataset.dlBlogName,
          blog_share_platform: target.dataset.dlBlogSharePlatform,
        };
        // Add blog_id if it exists, as it's optional
        if (target.dataset.dlBlogId) {
          eventDataForTracking.blog_id = target.dataset.dlBlogId;
        }
      }
      else if (eventName === 'custom_click_storefront') {
        // Construct payload specifically for custom_click_storefront as per user request
        eventDataForTracking = {
          data: this.extractClickData(event.target, false)
        };
      }
      else {
        // Original logic for other events, but with safer data handling
        const originalEventDataFromStore = dlData !== '{}' ? JSON.parse(dlData) : this.dataStore[sectionId];
        if (originalEventDataFromStore) {
            // Create a new object for tracking to avoid mutating the dataStore
            eventDataForTracking = { ...originalEventDataFromStore };
            if (eventDataForTracking.items &&
                Array.isArray(eventDataForTracking.items) && // Ensure it's an array
                eventDataForTracking.items[parseInt(index)] !== undefined) { // Ensure index is valid
                eventDataForTracking.items = [eventDataForTracking.items[parseInt(index)]]; // Use only the specific item
            }
        } else {
            eventDataForTracking = {}; // Fallback to empty object if no data in store
        }
      }

      this.track(eventName, eventDataForTracking);

    } catch (error) {
      this.log('Error handling click event:', error);
    }
  }

  /**
   * Register data for a specific section ID
   * @param {string} sectionId - The section ID
   * @param {Object} data - The data to store
   */
  registerData(sectionId, data) {
    this.log(`Registering data for section: ${sectionId}`, data);
    this.dataStore[sectionId] = data;
  }

  /**
   * Track an event
   * @param {string} eventName - The name of the event to track
   * @param {Object} eventData - The data to track with the event
   */
  track(eventName, eventData = {}) {
    // Format data for GA4 e-commerce events
    let formattedData = {
      ...eventData,
      ecommerce: {}
    };

    const eventFlag = {
      view_item_list: false,
      view_item: false,
      view_cart: false
    };
    
    // Handle e-commerce events specifically
    if (['view_item_list', 'select_item', 'view_item', 'add_to_cart', 'remove_from_cart', 'view_cart', 'begin_checkout', 'purchase'].includes(eventName)) {
      // Skip if it's a flagged event and already triggered
      if (eventFlag[eventName] !== undefined && eventFlag[eventName]) {
        return;
      }

      // Mark flagged event as triggered
      if (eventFlag[eventName] !== undefined) {
        eventFlag[eventName] = true;
      }

      formattedData = {
        ...formattedData,
        ecommerce: {
          market_language: window?.azfyMarket?.language || null,
          market_country: window?.azfyMarket?.country || null,
          items: [],
          ...eventData.ecommerce
        }
      };

      // Add list information for list-related events
      if (eventData.list_name || eventData.list_id) {
        formattedData.ecommerce.item_list_name = eventData.list_name || '';
      }
      
      // Format items array if present
      if (Array.isArray(eventData.items)) {
        formattedData.ecommerce.items = eventData.items.map(item => {
          const priceNum = typeof item.price === 'string'
              ? Number(item.price.replaceAll(',', '.'))
              : item.price;
          const compareAtPriceNum = typeof item.compare_at_price === 'string'
              ? Number(item.compare_at_price.replaceAll(',', '.') || item.price.replaceAll(',', '.'))
              : item.compare_at_price;
          return {
            item_name: item.item_name || '',
            item_id: String(item.item_id) || '',
            item_sku: item.item_sku || '',
            price: parseFloat(item.price) || 0,
            quantity: item.quantity || 1,
            item_category: item.item_category || '',
            item_variant: item.item_variant || '',
            item_variant_id: String(item.item_variant_id || ''),
            item_brand: item.item_brand || '',
            size: item.size || '',
            parent_item_id: item.parent_id || '',
            is_on_promotion: item.on_sale ? 'True' : 'False',
            product_deal_type: item.deal_type || 'Individual',
            bundle_item_name: '',
            in_stock: item.available ? 'True' : 'False',
            is_free_gift: item.is_gift ? 'True' : 'False',
            price_type: compareAtPriceNum > priceNum ? 'Discounted' : 'Full',
            original_item_price: item.compare_at_price || item.price || 0,
            discounted_item_price: item.price || 0,
            item_discount_percent:
                compareAtPriceNum > priceNum && compareAtPriceNum > 0
                    ? `${Math.round((1 - (priceNum / compareAtPriceNum)) * 100)}%`
                    : '0%'
          };
        });
      }
    }

    // if (this.options.debug) {
    //   this.log(`[DataLayerHelper] Tracking event: ${eventName}`, formattedData);
    // }
    // }
    // Use Shopify analytics if available
    const { analytics } = window.Shopify || {};
    if (analytics && typeof analytics.publish === 'function') {
      // Publish to Shopify's analytics system
      if (this.options.debug) {
        this.log(`Publishing to Shopify analytics: ${eventName}`, formattedData);
      }

      if(eventName === 'view_promotion'){
        setTimeout(() => {
          analytics.publish(eventName, formattedData);
        }, 1000);
      } else {
        analytics.publish(eventName, formattedData);
      }

      formattedData = {};
    }
  }

  /**
   * Log messages if debug mode is enabled
   * @param {string} message - The message to log
   * @param {*} data - Optional data to log
   */
  log(message, data) {
    if (this.options.debug) {
      if (data) {
        // console.log(`[DataLayerHelper] ${message}`, JSON.stringify(data, null, 2));
        console.log(`[DataLayerHelper] ${message}`, data);
      } else {
        console.log(`[DataLayerHelper] ${message}`);
      }
    }
  }
}

if (!window.dataLayerHelper) {
  window.dataLayerHelper = new DataLayerHelper({
    debug: true // Set to false in production
  });
}
